/**
 * Detects when a new build has been deployed and the running tab is on a
 * stale bundle, then reloads the page so the new JS is picked up (which in
 * turn lets versionGate.ts run its cleanup if SCHEMA_VERSION was bumped).
 *
 * How it works:
 *   1. At init, fetch /index.html and extract the hashed name of the entry
 *      script (Vite emits something like /assets/index-AbCdEf.js).
 *   2. On tab visibility change (user comes back to the tab) AND on a slow
 *      hourly timer, refetch /index.html and re-extract the hash.
 *   3. If it differs from the initial one, the deployed bundle is newer than
 *      the one this tab is running → window.location.reload().
 *
 * Skipped in dev (Vite HMR handles refresh).
 */
const POLL_INTERVAL_MS = 60 * 60 * 1000 // 1h
const BUNDLE_HASH_REGEX = /src=["'][^"']*\/assets\/index-([\w-]+)\.js/

let initialBundle: string | null = null
let reloadInFlight = false

async function readBundleHash(): Promise<string | null> {
  try {
    const res = await fetch('/index.html', {
      cache: 'no-store',
      credentials: 'omit',
    })
    if (!res.ok) return null
    const html = await res.text()
    const m = html.match(BUNDLE_HASH_REGEX)
    return m?.[1] ?? null
  } catch {
    return null
  }
}

async function checkAndReloadIfStale(): Promise<void> {
  if (reloadInFlight || initialBundle === null) return
  const current = await readBundleHash()
  if (!current || current === initialBundle) return
  reloadInFlight = true
  // Hard reload so the new bundle (and its versionGate) gets to run.
  window.location.reload()
}

export async function initVersionWatcher(): Promise<void> {
  if (typeof window === 'undefined') return
  if (import.meta.env.DEV) return

  initialBundle = await readBundleHash()
  if (!initialBundle) return // no baseline → nothing to compare against

  // Hourly heartbeat for tabs that stay visible without focus changes.
  setInterval(checkAndReloadIfStale, POLL_INTERVAL_MS)

  // The likely trigger in practice: user comes back to a tab they left open.
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') void checkAndReloadIfStale()
  })

  // Also check when the network comes back — they may have been offline
  // through the deploy.
  window.addEventListener('online', () => { void checkAndReloadIfStale() })
}
